import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import { FormCard, FormGrid } from "../FormCard";
import { InputRow, SelectRow, TextAreaRow } from "../Rows";
import { buildRelatedLocationsSummary } from "./locationCardSummaries";

const languageOptions = [
  { value: "sv", label: "sv" },
  { value: "en", label: "en" },
];

const createEmptyRelatedLocation = () => ({
  relatedLocationPublicId: null,
  latitude: "",
  longitude: "",
  language: "",
  text: "",
});

function RelatedLocationsEditor({ data = [], onChange = () => {} }) {
  const { t } = useTranslation();
  const [relatedLocations, setRelatedLocations] = useState(data);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    if (hasMounted) {
      onChange(relatedLocations);
    }
  }, [hasMounted, onChange, relatedLocations]);

  useEffect(() => {
    setRelatedLocations(data);
    if (!hasMounted) {
      setHasMounted(true);
    }
  }, [data, hasMounted]);

  const updateLocation = (index, key, value) => {
    setRelatedLocations((prev) =>
      prev.map((location, currentIndex) =>
        currentIndex === index ? { ...location, [key]: value } : location
      )
    );
  };

  const addLocation = () => {
    setRelatedLocations((prev) => [...prev, createEmptyRelatedLocation()]);
  };

  const removeLocation = (index) => {
    setRelatedLocations((prev) =>
      prev.filter((_, currentIndex) => currentIndex !== index)
    );
  };

  return (
    <FormCard
      title={t("site.location.relatedTitle")}
      description={t("site.location.relatedDescription")}
      summary={buildRelatedLocationsSummary(relatedLocations, t)}
      collapsible
      defaultOpen={false}
    >
      <div className="flex flex-col gap-4">
        {relatedLocations.map((location, index) => (
          <FormCard
            key={location.relatedLocationPublicId || `related-location-${index}`}
            title={
              <span className="inline-flex items-center gap-3">
                <span>{location.text || t("site.location.relatedLocationN", { n: index + 1 })}</span>
                <button
                  type="button"
                  className="text-error-500 transition hover:text-error-600"
                  onClick={() => removeLocation(index)}
                  title={t("site.location.removeRelatedLocation")}
                >
                  <FontAwesomeIcon icon={faTrash} className="w-4" />
                </button>
              </span>
            }
            description={
              location.relatedLocationPublicId
                ? t("site.location.updateRelatedLocation")
                : t("site.location.newRelatedLocation")
            }
            collapsible
            defaultOpen={true}
            titleClassName="pr-12"
          >
            <FormGrid>
              <InputRow
                label={t("common.latitude")}
                value={location.latitude}
                onChange={(value) => updateLocation(index, "latitude", value)}
                maxLength={9}
                />

              <InputRow
                label={t("common.longitude")}
                value={location.longitude}
                onChange={(value) => updateLocation(index, "longitude", value)}
                maxLength={9}
              />

              <SelectRow
                label={t("common.languageCode")}
                value={String(location.language || "").toLowerCase()}
                options={languageOptions.map((option) => ({
                  ...option,
                  label: t(`language.${option.value}`, { defaultValue: option.value }),
                }))}
                onChange={(value) =>
                  updateLocation(index, "language", value?.toLowerCase?.() ?? value)
                }
                isClearable
              />

              <TextAreaRow
                label={t("common.text")}
                value={location.text}
                rows={3}
                onChange={(value) => updateLocation(index, "text", value)}
              />
            </FormGrid>
          </FormCard>
        ))}

        <div>
          <button type="button" className="btn btn-primary" onClick={addLocation}>
            {t("site.location.addRelatedLocation")}
          </button>
        </div>
      </div>
    </FormCard>
  );
}

export default RelatedLocationsEditor;
